// Central team-option catalogue shared by Players + Transfers + Trades admin.
// Every team on the site: PWHL team_ids, NCAA schools, and every other league
// (USports, SDHL, Auroraliiga, Olympics, IIHF). Non-PWHL/NCAA values are stored
// as the team's display name (matches NCAA behaviour).
import { TEAM_INFO } from "./teamMap";
import { ALL_NCAA_SCHOOLS } from "./ncaaData";
import { LEAGUES as LEAGUE_DEFS, LEAGUE_IDS } from "./leagues";

export const PWHL_TEAM_OPTIONS = Object.keys(TEAM_INFO).map((id) => ({
    value: id, label: TEAM_INFO[id].name, sub: "PWHL", league: "PWHL",
}));

export const NCAA_TEAM_OPTIONS = ALL_NCAA_SCHOOLS.map((s) => ({
    value: s.school, label: s.school, sub: `NCAA · ${s.conference}`, league: "NCAA",
}));

// Team names for a leagues.js league id (flat list or conference-grouped).
export const leagueTeamNames = (lid) => {
    const lg = LEAGUE_DEFS[lid] || {};
    return lg.conferences ? lg.conferences.flatMap((c) => c.teams || []) : (lg.teams || []);
};

export const OTHER_LEAGUE_OPTIONS = LEAGUE_IDS.flatMap((lid) => {
    const lg = LEAGUE_DEFS[lid] || {};
    return leagueTeamNames(lid).map((n) => ({ value: n, label: n, sub: lg.name || lid, league: lg.name || lid }));
});

export const ALL_TEAM_OPTIONS = [
    ...PWHL_TEAM_OPTIONS,
    ...NCAA_TEAM_OPTIONS,
    ...OTHER_LEAGUE_OPTIONS,
].filter((o, i, arr) => arr.findIndex((x) => x.value === o.value) === i);

export const TEAM_OPT_BY_VALUE = Object.fromEntries(ALL_TEAM_OPTIONS.map((o) => [o.value, o]));
export const TEAM_OPT_BY_LABEL = Object.fromEntries(ALL_TEAM_OPTIONS.map((o) => [o.label.toLowerCase(), o]));

export const teamOptionLabel = (value) => (value ? (TEAM_OPT_BY_VALUE[value]?.label || value) : "");

// Map a player-form league string ("PWHL" | "NCAA" | "USports" | …) to its team
// options. Falls back to [] for free-text leagues.
export const LEAGUE_DEF_BY_NAME = Object.fromEntries(
    LEAGUE_IDS.map((lid) => [(LEAGUE_DEFS[lid].name || lid).toLowerCase(), lid]),
);

export const teamsForLeagueName = (name) => {
    const n = (name || "").trim();
    if (n === "PWHL") return PWHL_TEAM_OPTIONS;
    if (n === "NCAA") return NCAA_TEAM_OPTIONS;
    const lid = LEAGUE_DEF_BY_NAME[n.toLowerCase()];
    if (lid) return leagueTeamNames(lid).map((t) => ({ value: t, label: t, sub: name, league: name }));
    return [];
};

// The full league list for the player-form dropdown.
// "DNP" = Did Not Play — for players not currently active in any league (e.g.
// retired/away players who may return via the draft). DNP players need no team.
export const PLAYER_LEAGUE_OPTIONS = [
    "PWHL", "NCAA",
    ...LEAGUE_IDS.map((lid) => LEAGUE_DEFS[lid].name || lid),
    "DNP",
].filter((v, i, a) => a.indexOf(v) === i);

// Override key used by Team Info admin + public League pages for a non-PWHL/NCAA
// league team: `lg-<leagueId>-<teamName>`.
export const leagueOverrideKey = (leagueId, teamName) => `lg-${leagueId}-${teamName}`;
